import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react'
import './EditProjectModule.scss'
import { Col, Collapse, Form, Row, Switch, message } from 'antd'
import dateFormat from 'dateformat'
import { format } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import {
  DrawerModule,
  Button,
  SelectField,
  InputField,
  CustomizeModal,
} from '..'
import { getProjectById, updateProject } from '../../utils/api/projectServices'
import { fetchProjectOptionsService } from '../../utils/api/staticOptionsServices'
import { getPredictionDetailsById } from '../../utils/api/predictionAndInvoiceServices'
import { PopupConfirm } from '..'
import {
  setProjectBasedOptions,
  staticOptionsSelector,
} from '../../redux/reducer/getOptionsSlice'
import OtherDetailsPanel from '../OtherDetailsPanel/OtherDetailsPanel'
import ProjectDetailsPanel from '../ProjectDetailsPanel/ProjectDetailsPanel'
import {
  YES,
  NO,
  DATE_FORMATS,
  ignorableErrorFields,
  STATUS,
  TOAST,
  projectModel,
  payModel,
  afteFieldRetainerOptions,
  invoiceTemplateField,
  invoiceTemplateOptions,
  UserRoles,
} from '../../utils/constants/constant'
import {
  PLACEHOLDER_MESSAGES,
  TOASTIFY_MESSAGES,
  VALIDATION_MESSAGES,
} from '../../utils/constants/messages'
import { toastNotify } from '../../utils/helperFunctions/toastNotify'
import { useWatch } from 'antd/lib/form/Form'
import {
  invoiceCycleRule,
  invoiceTemplateRule,
  leaveTermRule,
  projectModelRule,
  retainerRule,
} from '../../utils/constants/validationRules'
import AddonAfterField from '../AddonAfterField/AddonAfterField'
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg'
import { ReactComponent as AddIcon } from '../../assets/icons/plus.svg'
import { AuthContext } from '../../store/auth'
import useBillingRules from '../../utils/hooks/useAddBillingDetailRules'

const { Panel } = Collapse

const EditProjectModule = ({
  projectId,
  open,
  onClose,
  onChange,
  projectData,
  primaryCsm,
  secondaryCsmList,
  primaryAccountant,
  secondaryAccountantList,
}) => {
  const [document, setDocument] = useState({ addFiles: [], removeFiles: [] })
  let projectStartDate
  const [formDisabled, setFormDisabled] = useState(false)
  const [prjStartDate, setPrjStartDate] = useState('')
  const [show, setShow] = useState(true)
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [isFormDataChanged, setIsFormDataChanged] = useState(false)
  const [isDisabledUpdate, setIsDisabledUpdate] = useState(false)
  const [isValidSize, setIsValidSize] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [payModelFieldName, setPayModelFieldName] = useState()
  const [activeBillingKey, setActiveBillingKey] = useState(null)
  const [isDisableAdd, setIsDisableAdd] = useState({
    salesPersons: false,
    secondaryCsm: false,
    accountantsList: false,
  })
  const [isShowFields, setIsShowFields] = useState([])
  const [form] = Form.useForm()
  const hasShownMultiModelConflictRef = useRef(false)
  const dispatch = useDispatch()
  const { userRole } = useContext(AuthContext)
  const selectedCsm = projectData?.csmUsers
  const projectModelFieldValue = useWatch('projectModel', form)
  const payModelFieldValue = useWatch('payModel', form)
  const isMultiModelEnabledFieldValue = useWatch('isMultiModelEnabled', form)
  const billingConfigFieldValue = useWatch('projectConfigurationDetails', form)
  const projectMultiModelEnabled = Boolean(projectData?.isMultiModelEnabled)

  // below useEffect will hide/unhide Quantity field initially
  useEffect(() => {
    const isShowRetainer = Boolean(
      projectModelFieldValue?.includes(projectModel.TMRetainer) &&
      payModelFieldValue === payModel.prepaid
    )
    const isShowInvoiceTemplate = Boolean(
      projectModelFieldValue?.includes(projectModel.hourly)
    )
    setIsShowFields((prevState) => {
      return {
        ...prevState,
        retainer: isShowRetainer,
        invoiceTemplate: isShowInvoiceTemplate,
      }
    })
    form.setFieldValue('tmHours', projectData?.tmHours)

    const projectConfigurationDetails =
      projectData?.projectConfigurationDetails || []
    const initialConfigValues = projectConfigurationDetails.map((config) => ({
      id: config?.id || 0,
      projectModel: config.projectModelName || null,
      cycle: config.invoiceCycleName || null,
      payModel: config.payModel || null,
      invoiceTerm: config.invoiceTerm || 0,
      leaveTerm: config.leaveTerm || 0,
      retainer: config?.retainer || null,
      tmHours: config?.tmHours || null,
    }))

    form.setFieldsValue({
      ...initialConfigValues,
      projectConfigurationDetails: [...initialConfigValues],
    })

    // Initialize isShowFields for each panel
    setIsShowFields(
      initialConfigValues.map((config) => ({
        retainer: Boolean(
          config.projectModel?.includes(projectModel.TMRetainer) &&
          config.payModel === payModel.prepaid
        ),
        invoiceTemplate: Boolean(
          config.projectModel?.includes(projectModel.hourly)
        ),
      }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectModelFieldValue, payModelFieldValue, projectData])

  const {
    accTypeOptions,
    invoiceCycleOptions,
    payModelOptions,
    projectModelOptions,
    csmOptions,
    accountantOptions,
    leadSourceOptions,
    marketingChannelOptions,
    activeBillingOptions,
    salesPersonOptions,
  } = useSelector(staticOptionsSelector)
  const projectDetailsOptions = [
    accTypeOptions,
    invoiceCycleOptions,
    payModelOptions,
    projectModelOptions,
    csmOptions,
    accountantOptions,
    leadSourceOptions,
    marketingChannelOptions,
    activeBillingOptions,
    salesPersonOptions,
  ]
  const hasEmptyOptions = projectDetailsOptions.some(
    (option) => option?.length === 0
  )
  const getProjectDetailOptions = async () => {
    const projectsOptions = await fetchProjectOptionsService()
    dispatch(setProjectBasedOptions(projectsOptions))
  }

  const {
    filteredProjectModelOptions,
    isAddBillingDisabled,
    isMultiModelToggleDisabled,
  } = useBillingRules({
    form,
    projectModelOptions,
    userRole,
    projectData,
  })

  // will fetch required options if one of them is empty
  useEffect(() => {
    if (hasEmptyOptions) {
      getProjectDetailOptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const projectStartDateString = projectData?.projectStartDate
    const dates = ['projectStartDate']
    const dateStrings = [projectStartDateString]

    dates.forEach((item, index) => {
      onDateChange(null, dateStrings[index], item)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData])

  const updateProjectBasedOptions = async () => {
    const projectsOptions = await fetchProjectOptionsService()
    dispatch(setProjectBasedOptions(projectsOptions))
  }

  function hasDuplicates(salesPersons) {
    const checkSet = new Set(salesPersons)

    return salesPersons.length !== checkSet.size
  }

  const buildCsmData = (values) => {
    const selectedCsmData = [{ email: values.csm, primary: true }]
    const selectedCsmList = [values.csm]

    if (values?.secondaryCsm) {
      values.secondaryCsm.forEach((item) => {
        selectedCsmData.push({ email: item?.csm, primary: false })
        selectedCsmList.push(item?.csm)
      })
    }

    return { selectedCsmData, selectedCsmList }
  }

  function buildAccountantUsers(values) {
    const selectedAccountantList =
      values?.accountantsList?.map((item) => ({
        email: item?.accountant,
        primary: false,
      })) || []

    return [
      ...selectedAccountantList,
      { email: values.accountant, primary: true },
    ]
  }

  function collectSalesPersons(values) {
    const salesPersons = []

    Object.keys(values).forEach((key) => {
      if (key.includes('salesPerson')) {
        salesPersons.push(values[key])
      }
    })

    if (values?.salesmans) {
      values.salesmans.forEach((item) => {
        salesPersons.push(item.salesperson)
      })
    }

    return salesPersons
  }

  function getSalesPersonValidationMessage({
    salesPersons,
    sameSalesmans,
    sameCsm,
    sameAccoutants,
  }) {
    if (salesPersons.length <= 0) {
      return TOASTIFY_MESSAGES.oneSalesmanMessage
    }
    if (sameSalesmans && sameCsm && sameAccoutants) {
      return TOASTIFY_MESSAGES.uniqueNameMessage
    }
    if (sameSalesmans) {
      return TOASTIFY_MESSAGES.sameSalesPersonsMessage
    }
    if (sameCsm) {
      return TOASTIFY_MESSAGES.sameCsmMessage
    }
    if (sameAccoutants) {
      return TOASTIFY_MESSAGES.sameAccountantMessage
    }
    return null
  }

  const getMultiModelValidationState = useCallback(
    (values = {}) => {
      const billingConfigs = values?.projectConfigurationDetails || []
      const activeConfigCount = billingConfigs.filter(
        (config) => config?.active !== false
      ).length
      const effectiveIsMultiModelEnabled =
        typeof values?.isMultiModelEnabled === 'boolean'
          ? values?.isMultiModelEnabled
          : projectMultiModelEnabled

      return {
        hasMultiModelConflict:
          !effectiveIsMultiModelEnabled && activeConfigCount > 1,
      }
    },
    [projectMultiModelEnabled]
  )

  useEffect(() => {
    const { hasMultiModelConflict } = getMultiModelValidationState({
      isMultiModelEnabled: isMultiModelEnabledFieldValue,
      projectConfigurationDetails: billingConfigFieldValue,
    })

    if (userRole !== UserRoles.ADMIN) {
      form.setFields([
        {
          name: ['isMultiModelEnabled'],
          errors: [],
        },
      ])

      if (hasMultiModelConflict && !hasShownMultiModelConflictRef.current) {
        message.error(VALIDATION_MESSAGES.multiModelSingleActiveMessage)
        hasShownMultiModelConflictRef.current = true
      }

      if (!hasMultiModelConflict) {
        hasShownMultiModelConflictRef.current = false
      }

      return
    }

    form.setFields([
      {
        name: ['isMultiModelEnabled'],
        errors: hasMultiModelConflict
          ? [VALIDATION_MESSAGES.multiModelSingleActiveMessage]
          : [],
      },
    ])
  }, [
    isMultiModelEnabledFieldValue,
    billingConfigFieldValue,
    form,
    getMultiModelValidationState,
    userRole,
  ])

  async function onFinish(values) {
    const { selectedCsmData, selectedCsmList } = buildCsmData(values)
    const accountantUsers = buildAccountantUsers(values)
    const salesPersons = collectSalesPersons(values)

    const sameSalesmans = hasDuplicates(salesPersons)
    const sameCsm = hasDuplicates(selectedCsmList)
    const sameAccoutants = hasDuplicates(
      accountantUsers?.map((acc) => acc?.email)
    )

    const validationMessage = getSalesPersonValidationMessage({
      salesPersons,
      sameSalesmans,
      sameCsm,
      sameAccoutants,
    })

    if (validationMessage) {
      toastNotify(TOAST.error, validationMessage)
      return
    }

    if (!isValidSize) {
      return
    }

    const { hasMultiModelConflict } = getMultiModelValidationState(values)

    if (hasMultiModelConflict) {
      form.setFields([
        {
          name: ['isMultiModelEnabled'],
          errors: [VALIDATION_MESSAGES.multiModelSingleActiveMessage],
        },
      ])
      message.error(VALIDATION_MESSAGES.multiModelSingleActiveMessage)
      return
    }

    setFormDisabled(true)

    const activeBillingFlag = values.activeBillingFlag === YES

    const projectStartDate = dateFormat(
      form.getFieldsValue().projectStartDate,
      DATE_FORMATS.dd_mmm_yyyy
    )
    const salesPersonIds = salesPersons?.map((salesPersonId) => ({
      id: salesPersonId,
    }))
    // Prepare project configuration details
    const projectConfigurationDetails =
      values.projectConfigurationDetails?.map((config) => ({
        id: config?.id || 0,
        projectModelName: config.projectModel,
        invoiceCycleName: config.cycle,
        invoiceTerm: Number(config.invoiceTerm) || 0,
        payModel: config.payModel,
        leaveTerm: config.leaveTerm || 0,
        invoiceTemplate:
          config.invoiceTemplate || invoiceTemplateField.TECHWISE,
        retainer: Number(config.retainer),
        tmHours: config.tmHours,
        active: config.active,
      }))
    const payload = {
      projectId: parseInt(projectId),
      name: values.projectName,
      zohoProjectId: values.zohoProjectId,
      model: values.projectModel,
      cycle: values.cycle,
      invoiceTerm: values.invoiceTerm,
      payModel: values.payModel,
      accType: values.accType,
      projectStartDate,
      csmUsers: selectedCsmData,
      accountantUsers,
      salesPersons: salesPersonIds,
      leadSource: values.leadSource,
      channel: values.marketingChannel,
      activeBillingFlag,
      invoiceTemplate: values.invoiceTemplate,
      leaveTerm: values.leaveTerm,
      projectConfigurationDetails,
      retainerActiveBalance: values?.retainerActiveBalance,
      enableInvoiceOverdueNotification:
        values?.enableInvoiceOverdueNotification,
      isMultiModelEnabled:
        typeof values?.isMultiModelEnabled === 'boolean'
          ? values?.isMultiModelEnabled
          : Boolean(projectData?.isMultiModelEnabled),
      retainerActiveDate: Boolean(values?.retainerActiveBalance)
        ? format(
          new Date(values?.retainerActiveDate),
          DATE_FORMATS.dd_MMM_yyyy
        )
        : null,
      currencyCode: 'USD',
    }
    const response = await updateProject(payload, document)
    if (response?.status === STATUS.ok) {
      setFormDisabled(false)
      toastNotify(TOAST.success, response?.data?.message)
      onClose()
      form.resetFields()
      setShow(true)
      setIsDisabledUpdate(true)
      setIsFormDataChanged(false)
      updateProjectBasedOptions()
      dispatch(getProjectById(projectId))
      dispatch(getPredictionDetailsById(projectId))
    } else {
      setFormDisabled(false)
      toastNotify(
        TOAST.error,
        response?.data?.message ?? TOASTIFY_MESSAGES?.wentWrongMessage
      )
    }
  }

  function onFinishFailed() {
    toastNotify(TOAST.error, TOASTIFY_MESSAGES.failedUpdateProjectMessage)
  }

  const onDateChange = (date, dateString, name) => {
    const data = dateString && dateFormat(dateString, DATE_FORMATS.dd_mmm_yyyy)
    if (name === 'projectStartDate') {
      projectStartDate = data
      setPrjStartDate(projectStartDate ? moment(projectStartDate) : '')
    }
  }

  const handleFormChange = (changedFields, allFields) => {
    const projectConfigDetails =
      form.getFieldValue('projectConfigurationDetails') || []
    const updatedIsShowFields = projectConfigDetails.map((config) => ({
      retainer: Boolean(
        config?.projectModel?.includes(projectModel.TMRetainer) &&
        config?.payModel === payModel.prepaid
      ),
      invoiceTemplate: Boolean(
        config?.projectModel?.includes(projectModel.hourly)
      ),
    }))

    setIsShowFields(updatedIsShowFields)

    // Validate fields and disable update button if necessary
    const ignorableErrorFieldsArr = Object.values(ignorableErrorFields)
    const errorFields = allFields.filter(
      (field) =>
        !ignorableErrorFieldsArr.includes(field.name[0]) &&
        (field.errors.length ||
          field.value === undefined ||
          field.value === null)
    )

    // Check for errors in all fields
    const hasErrors = allFields.some(
      (field) =>
        field.name?.[0] !== 'isMultiModelEnabled' && field.errors?.length > 0
    )
    const billingRequiredFields = [
      'projectModel',
      'cycle',
      'payModel',
      'leaveTerm',
    ]
    // Check if any billing configuration is missing required fields
    const hasInvalidBillingConfig = projectConfigDetails.some((config) => {
      return billingRequiredFields.some((fieldName) => {
        const value = config[fieldName]
        return value === undefined || value === null || value === ''
      })
    })

    const hasInvalidActiveBillingConfig = projectConfigDetails
      .filter((config) => config?.active !== false)
      .some((config) => {
        return billingRequiredFields.some((fieldName) => {
          const value = config[fieldName]
          return value === undefined || value === null || value === ''
        })
      })

    const currentValues = form.getFieldsValue(true)
    const { hasMultiModelConflict } =
      getMultiModelValidationState(currentValues)

    // Check dynamic fields for errors
    const dynamicFields = changedFields.filter((field) =>
      ignorableErrorFieldsArr.includes(field.name[0])
    )

    const dynamicErrorFields = dynamicFields.filter((field) => {
      const fieldValues = field.value
      if (
        Array.isArray(fieldValues) &&
        fieldValues.some((value) => value === undefined)
      ) {
        switch (field.name[0]) {
          case ignorableErrorFields.salesmans:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              salesPersons: true,
            }))
            break
          case ignorableErrorFields.secondaryCsm:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              secondaryCsm: true,
            }))
            break
          case ignorableErrorFields.accountantsList:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              accountantsList: true,
            }))
            break
          default:
            break
        }
        return true
      } else {
        switch (field.name[0]) {
          case ignorableErrorFields.salesmans:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              salesPersons: false,
            }))
            break
          case ignorableErrorFields.secondaryCsm:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              secondaryCsm: false,
            }))
            break
          case ignorableErrorFields.accountantsList:
            setIsDisableAdd((prevState) => ({
              ...prevState,
              accountantsList: false,
            }))
            break
          default:
            break
        }
        return false
      }
    })

    if (Array.isArray(dynamicErrorFields)) {
      errorFields.push(...dynamicErrorFields)
    }

    setIsDisabledUpdate(
      hasErrors ||
      (projectConfigDetails.some((config) => config?.active !== false)
        ? hasInvalidActiveBillingConfig
        : hasInvalidBillingConfig) ||
      dynamicErrorFields.length > 0 ||
      hasMultiModelConflict
    )
  }

  const onSwitchChange = (checked, field) => {
    // get current list from form
    const configs = form.getFieldValue('projectConfigurationDetails') || []
    const idx = Number(field.name) // safe index

    // count other active configs (exclude the current one)
    const otherActiveCount = configs.reduce((acc, cfg, i) => {
      if (i === idx) return acc
      return acc + (cfg?.active ? 1 : 0)
    }, 0)

    // If user is trying to deactivate this switch AND there are 0 other actives,
    // this is the last active -> block and notify
    if (!checked && otherActiveCount === 0) {
      toastNotify(
        TOAST.warning,
        'At least one Billing Detail must remain active.'
      )
      // ensure UI/form stays as Active (revert)
      form.setFieldValue(['projectConfigurationDetails', idx, 'active'], true)
      return
    }

    // otherwise allow change
    form.setFieldValue(['projectConfigurationDetails', idx, 'active'], checked)
  }

  // when change paymodel for exiting configuration
  const onPayModelChange = (value, projectConfig, fieldName) => {
    if (projectConfig?.id) {
      setIsModalOpen(true)
      setPayModelFieldName(fieldName)
    }
  }

  const defaultBillingConfig = {
    projectModel: null,
    cycle: null,
    invoiceTerm: 0,
    payModel: null,
    leaveTerm: 0,
    invoiceTemplate: invoiceTemplateField.TECHWISE,
    retainer: null,
    tmHours: null,
    active: true,
  }
  return (
    <DrawerModule
      size={'600px'}
      open={open}
      onClose={onClose}
      title={'Edit Project'}
      closable={!isPopupVisible}
      destroyOnClose
      className="edit-project-drawer"
    >
      <Form
        layout="vertical"
        className="add-project"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError
        onFieldsChange={(ChangedFields, allFields) => {
          setIsFormDataChanged(true)
          handleFormChange(ChangedFields, allFields)
        }}
        disabled={formDisabled}
      >
        <div className="add-project-details">
          <h3>Project Details</h3>
          <ProjectDetailsPanel
            projectData={projectData}
            form={form}
            isShowFields={isShowFields}
            accTypeOptions={accTypeOptions}
            onDateChange={onDateChange}
            prjStartDate={prjStartDate}
            isMultiModelToggleDisabled={isMultiModelToggleDisabled}
          />
        </div>
        <Collapse defaultActiveKey={[1]} onChange={onChange}>
          <Form.List
            name="projectConfigurationDetails"
            initialValue={
              projectData?.projectConfigurationDetails?.length > 0
                ? [...projectData.projectConfigurationDetails]?.sort(
                  (a, b) => (b.active === true) - (a.active === true)
                )
                : [defaultBillingConfig]
            }
          >
            {(fields, { add, remove }) => (
              <>
                <Collapse
                  accordion
                  activeKey={activeBillingKey}
                  onChange={(key) => {
                    setActiveBillingKey(key)
                  }}
                  className="biling-details-collapse"
                >
                  {fields.map((field, index) => {
                    const currentConfig = form.getFieldValue([
                      'projectConfigurationDetails',
                      field.name,
                    ])
                    const uniqueKey = currentConfig?.id || `new-${field.name}`
                    const headerName = projectData
                      ?.projectConfigurationDetails?.[index]?.id
                      ? `(${projectData?.projectConfigurationDetails?.[index]?.projectModelName} - ${projectData?.projectConfigurationDetails?.[index]?.payModel})`
                      : '(New)'
                    return (
                      <Panel
                        header={`Billing Details - ${headerName}`}
                        key={uniqueKey}
                        extra={
                          projectData?.projectConfigurationDetails?.[index]
                            ?.id ? (
                            <Form.Item
                              name={[field.name, 'active']}
                              valuePropName="checked"
                              initialValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.active
                              }
                            >
                              <Switch
                                checkedChildren={'Active'}
                                unCheckedChildren={'Inactive'}
                                onClick={(_, e) => {
                                  e.stopPropagation()
                                }}
                                onChange={(checked) =>
                                  onSwitchChange(checked, field)
                                }
                              />
                            </Form.Item>
                          ) : (
                            <Button
                              className="detete-icon"
                              title={'Delete Billing Configuration'}
                              aria-label="Delete billing configuration"
                              disabled={fields.length === 1}
                              onClick={(e) => {
                                e.stopPropagation()
                                remove(field.name)
                                // Reset activeBillingKey to null after deletion
                                setActiveBillingKey(null)
                              }}
                              icon={<DeleteIcon aria-hidden="true" />}
                            />
                          )
                        }
                      >
                        <Row gutter={16}>
                          {/* Project Model */}
                          <Col lg={12} md={12} sm={12}>
                            <SelectField
                              name={[field.name, 'projectModel']}
                              label="Select Project Model"
                              options={filteredProjectModelOptions}
                              existingValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.model
                              }
                              placeholder={
                                PLACEHOLDER_MESSAGES.projectModelMessage
                              }
                              value={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.model
                              }
                              required
                              type="text"
                              rules={projectModelRule}
                              isFeatured
                            />
                          </Col>

                          {/* Invoice Cycle */}
                          <Col lg={12} md={12} sm={12}>
                            <SelectField
                              name={[field.name, 'cycle']}
                              label="Invoice Cycle"
                              options={invoiceCycleOptions}
                              existingValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.cycle === '-'
                                  ? null
                                  : projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.cycle
                              }
                              type="text"
                              placeholder={
                                PLACEHOLDER_MESSAGES.invoiceCycleMessage
                              }
                              required
                              rules={invoiceCycleRule}
                            />
                          </Col>

                          {/* Invoice Term */}
                          <Col lg={12} md={12} sm={12}>
                            <InputField
                              name={[field.name, 'invoiceTerm']}
                              label="Invoice Term"
                              placeholder={
                                PLACEHOLDER_MESSAGES.invoiceTermMessage
                              }
                              type="number"
                              existingValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.invoiceTerm
                              }
                              disabled={true}
                            />
                          </Col>

                          {/* Pay Model */}
                          <Col lg={12} md={12} sm={12}>
                            <SelectField
                              name={[field.name, 'payModel']}
                              label="Pay Model"
                              options={payModelOptions}
                              existingValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.payModel === '-'
                                  ? null
                                  : projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.payModel
                              }
                              placeholder={PLACEHOLDER_MESSAGES.payModelMessage}
                              type="text"
                              required
                              rules={[
                                {
                                  required: true,
                                  message: VALIDATION_MESSAGES.payModelMessage,
                                },
                              ]}
                              onChange={(value) =>
                                onPayModelChange(
                                  value,
                                  projectData?.projectConfigurationDetails?.[
                                  index
                                  ],
                                  field.name
                                )
                              }
                            />
                          </Col>

                          {/* Leave Term */}
                          <Col lg={12} md={12} sm={12}>
                            <InputField
                              name={[field.name, 'leaveTerm']}
                              label="Leave Term"
                              placeholder={
                                PLACEHOLDER_MESSAGES.leaveTermMessage
                              }
                              type="number"
                              existingValue={
                                projectData?.projectConfigurationDetails?.[
                                  index
                                ]?.leaveTerm
                              }
                              required
                              rules={leaveTermRule}
                              step={0.01}
                              precision={2}
                            />
                          </Col>

                          {/* Conditional - Invoice Template */}
                          {isShowFields[index]?.invoiceTemplate && (
                            <Col lg={12} md={12} sm={12}>
                              <SelectField
                                name={[field.name, 'invoiceTemplate']}
                                label="Invoice Template"
                                options={invoiceTemplateOptions}
                                isFeatured={true}
                                existingValue={
                                  projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.invoiceTemplate === undefined
                                    ? invoiceTemplateField.TECHWISE
                                    : projectData
                                      ?.projectConfigurationDetails?.[index]
                                      ?.invoiceTemplate
                                }
                                defaultValue={
                                  projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.invoiceTemplate === undefined
                                    ? invoiceTemplateField.TECHWISE
                                    : projectData
                                      ?.projectConfigurationDetails?.[index]
                                      ?.invoiceTemplate
                                }
                                placeholder={
                                  PLACEHOLDER_MESSAGES.invoiceTemplateMessage
                                }
                                required
                                type="text"
                                rules={invoiceTemplateRule}
                              />
                            </Col>
                          )}

                          {/* Conditional - Retainer */}
                          {isShowFields[index]?.retainer && (
                            <Col lg={12} md={12} sm={12}>
                              <AddonAfterField
                                name={[field.name, 'retainer']}
                                label="Retainer"
                                required={true}
                                afteFieldOptions={afteFieldRetainerOptions}
                                defaultAfterFieldValue={
                                  projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.tmHours ?? true
                                }
                                initialValue={
                                  projectData?.projectConfigurationDetails?.[
                                    index
                                  ]?.retainer
                                }
                                rules={retainerRule}
                                onChange={(value) => {
                                  form.setFieldValue(
                                    [
                                      'projectConfigurationDetails',
                                      field.name,
                                      'tmHours',
                                    ],
                                    value
                                  )
                                }}
                                afterFieldName={[
                                  'projectConfigurationDetails',
                                  field.name,
                                  'tmHours',
                                ]}
                                form={form}
                              />
                            </Col>
                          )}
                        </Row>
                      </Panel>
                    )
                  })}
                </Collapse>
                <Button
                  onClick={() => {
                    add(defaultBillingConfig) // Add the new billing detail
                    setActiveBillingKey(`new-${fields.length}`)
                  }}
                  type="link"
                  className="add-package"
                  label={`Add Billing Details`}
                  icon={<AddIcon />}
                  disabled={isAddBillingDisabled}
                />
              </>
            )}
          </Form.List>

          <Panel header="Other Details" key="3" forceRender>
            <OtherDetailsPanel
              csmOptions={csmOptions}
              projectData={projectData}
              accountantOptions={accountantOptions}
              setShow={setShow}
              setDocument={setDocument}
              show={show}
              salesPersonOptions={salesPersonOptions}
              setIsValidSize={setIsValidSize}
              isValidSize={isValidSize}
              leadSourceOptions={leadSourceOptions}
              marketingChannelOptions={marketingChannelOptions}
              activeBillingOptions={activeBillingOptions}
              form={form}
              selectedCsm={selectedCsm}
              primaryCsm={primaryCsm}
              secondaryCsmList={secondaryCsmList}
              primaryAccountant={primaryAccountant}
              secondaryAccountantList={secondaryAccountantList}
              isDisableAdd={isDisableAdd}
            />
          </Panel>
        </Collapse>
        <div className="drawer-button btn-group">
          <PopupConfirm
            onConfirm={() => {
              form.resetFields()
              setIsPopupVisible(false)
              setIsDisableAdd({
                salesPersons: false,
                secondaryCsm: false,
              })
              setPrjStartDate(
                moment(projectData?.projectStartDate, DATE_FORMATS.DD_MMM_YYYY)
              )
              const initialConfigValues = [
                ...(projectData?.projectConfigurationDetails || []),
              ]
                ?.sort((a, b) => (b.active === true) - (a.active === true))
                ?.map((config) => ({
                  id: config?.id || 0,
                  invoiceCycleId: config?.invoiceCycleId,
                  projectModelId: config?.projectModelId,
                  projectModel: config.projectModelName || null,
                  cycle: config.invoiceCycleName || null,
                  payModel: config.payModel || null,
                  invoiceTerm: config.invoiceTerm || 0,
                  leaveTerm: config.leaveTerm || 0,
                  invoiceTemplate:
                    config.invoiceTemplate || invoiceTemplateField.TECHWISE,
                  retainer: config.retainer || null,
                  tmHours: config.tmHours || null,
                }))
              setIsDisabledUpdate(false)
              form.setFieldsValue({
                ...projectData,
                projectConfigurationDetails: initialConfigValues,
              })
            }}
            onCancel={() => setIsPopupVisible(false)}
            cancelText={NO}
            okText={YES}
            title={'Do you want to discard the changes?'}
          >
            <Button
              danger
              label="Cancel"
              disabled={!isFormDataChanged}
              onClick={() => {
                setIsPopupVisible(!isPopupVisible)
              }}
            />
          </PopupConfirm>
          <Button
            type="primary"
            label={'Update'}
            htmlType="submit"
            disabled={isDisabledUpdate}
          />
        </div>
      </Form>
      <CustomizeModal
        title="Warning !"
        centered={true}
        open={isModalOpen}
        size={900}
        handleModalOk={() => setIsModalOpen(false)}
        handleModalCancel={() => {
          setIsModalOpen(false)
          form.setFieldValue(
            ['projectConfigurationDetails', payModelFieldName, 'payModel'],
            projectData?.projectConfigurationDetails?.[payModelFieldName]
              ?.payModel
          )
        }}
        setShowSearchField={setIsModalOpen}
        okText="Yes, Continue"
        cancelText="No"
        closable={false}
        className={'paymodel-modal'}
      >
        <p>
          If the Pay Model is changed, any existing Predictions or Invoices
          associated with the previous Model may be impacted.
        </p>
      </CustomizeModal>
    </DrawerModule>
  )
}

export default EditProjectModule
